<template>
  <BasePopup
    ref="popup"
    class="card-edit-popup"
    title="Edit your credit card"
    :compact="true"
  >
    <p>
      If you want to update your credit card used for your subscription, please
      do it below.
    </p>

    <form class="card-edit-popup__form" @submit="submitForm">
      <StripeCard
        v-if="popup?.isVisible && billing.cardEditMode"
        ref="stripeCard"
        :billing="billing"
        :show-button="false"
      />
      <StripeCardInfo v-else ref="stripeCardInfo" :billing="billing" />
      <button
        v-wait:disabled="'Saving'"
        type="submit"
        class="card-edit-popup__btn btns btns--large"
      >
        <v-wait for="Saving">
          <template v-slot:waiting>
            Saving
            <button-spinner />
          </template>
          <span>Save</span>
        </v-wait>
      </button>
      <FormError :errors="validation.errors" :field="billing.plan.id" />
    </form>
  </BasePopup>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from 'vue'
import { useWait } from '@/app/composables/useWait'

import type { BillingView } from '@/models/billing'
import { ValidationState } from '@/helpers/form'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import StripeCard from '@/app/components/billing/StripeCard.vue'
import StripeCardInfo from '@/app/components/billing/StripeCardInfo.vue'
import BasePopup from '@/components/ui/BasePopup.vue'

const props = defineProps<{
  billing: BillingView
}>()

const wait = useWait()

const popup = useTemplateRef('popup')
const stripeCard = useTemplateRef('stripeCard')

const validation = ref(new ValidationState())

function show(): void {
  props.billing.cardEditMode = true
  popup.value?.show()
}

function hide(): void {
  props.billing.cardEditMode = false
  popup.value?.hide()
}

async function submitForm(event: Event): Promise<void> {
  event.preventDefault()
  await subscribe()
}

async function subscribe(): Promise<void> {
  try {
    wait.start('Saving')
    let hideDialog: boolean = true
    if (props.billing.cardEditMode && stripeCard.value?.needsSaving()) {
      const result = await stripeCard.value?.save()
      if (result !== true) {
        hideDialog = false
      }
    }
    if (hideDialog) {
      hide()
    }
    wait.end('Saving')
  } catch (error) {
    wait.end('Saving')
    validation.value.showErrors(error)
  }
}

defineExpose({
  show,
  hide,
})
</script>

<style lang="scss" scoped>
.card-edit-popup {
  :deep(.popup__header) {
    border-bottom: 1px solid $c-border-4;
    margin-bottom: 28px;
  }

  :deep(.popup__title) {
    margin-top: 32px;
    margin-bottom: 28px;

    @media (max-width: $w-medium-s) {
      margin-top: 0;
    }
  }

  &__form {
    margin-top: 28px;
  }

  &__btn {
    font-weight: 500;
    width: 100%;
  }
}
</style>
